import noop from 'lodash/noop'
// Dropzone hook.
// https://twitter.com/Jeffijoe
// Copyright (C) Jeff Hansen — Jeffijoe 2019 to present.
// MIT Licensed.
import { MutableRefObject, useReducer, useRef, useEffect } from 'react'
import { useRefDep } from './ref-dep'

/**
 * Typings for the state and actions.
 *
 * @typedef {Object} State
 * @property {boolean} isOver
 *
 * @typedef {Object} Action
 * @property {'enter' | 'leave'} type
 */

/**
 * Initial state.
 *
 * @type {State}
 */
const initialState = {
  isOver: false,
}

interface State {
  isOver: boolean
}

interface Action {
  type: 'enter' | 'leave'
}

/**
 * Reducer for the drag state. Ensures
 * we don't change the state if there's no reason to.
 *
 * @param {State} state
 * @param {Action} action
 */
function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'enter': {
      if (state.isOver) {
        return state
      }
      return {
        isOver: true,
      }
    }
    case 'leave': {
      if (!state.isOver) {
        return state
      }
      return {
        isOver: false,
      }
    }
    default: {
      return state
    }
  }
}

type FilesDroppedFunc = (files: FileList) => unknown

/**
 * Dropzone hook.
 *
 * @param {(files: FileList) => unknown} onFilesDropped called on drop. If falsy, disables drag-drop.
 * @returns {[State, React.RefObject<Node>]} a tuple containing the state and the target ref
 */
export function useDropzone<T>(
  onFilesDropped: FilesDroppedFunc,
  onEnter: () => void = noop,
  onLeave: () => void = noop,
): [State, MutableRefObject<(T extends HTMLElement ? T : HTMLElement) | null>] {
  /**
   * Returned to the caller to register the drop target.
   * @type {React.RefObject<Node>}
   */
  const targetRef = useRef<(T extends HTMLElement ? T : HTMLElement) | null>(
    null,
  )

  // Used for keeping track of drag counts.
  // Whenever child elements are dragEntered, they trigger a dragLeave on the parent.
  // This lets us keep count of those and only dispatch actions when the actual target is
  // dragLeave'd.
  const dragCount = useRef(0)

  // The drag state.
  const [state, dispatch] = useReducer<typeof reducer>(reducer, initialState)

  // Registration of the handlers.
  useEffect(() => {
    const element = targetRef.current
    // Leaving out the onFilesDropped callback is the same as disabling dragging.
    if (!onFilesDropped || !element) {
      return
    }
    element.addEventListener('dragenter', onDragEnter)
    element.addEventListener('dragleave', onDragLeave)
    element.addEventListener('dragover', onDragOver)
    element.addEventListener('drop', onDrop)
    return () => {
      element.removeEventListener('dragenter', onDragEnter)
      element.removeEventListener('dragleave', onDragLeave)
      element.removeEventListener('dragover', onDragOver)
      element.removeEventListener('drop', onDrop)
    }

    /**
     * Drag enter handler.
     *
     * @param {DragEvent} e
     */
    function onDragEnter(e: DragEvent) {
      e.preventDefault()
      if (!isFileDrag(e)) {
        return
      }
      incrementDragCount()
      e.dataTransfer!.dropEffect = 'copy'
    }

    /**
     * Drag over handler. Needs to be there to disable the default drop behavior.
     *
     * @param {DragEvent} e
     */
    function onDragOver(e: DragEvent) {
      e.preventDefault()
    }

    /**
     * Drag leave handler.
     *
     * @param {DragEvent} e
     */
    function onDragLeave(e: DragEvent) {
      e.preventDefault()
      if (!isFileDrag(e)) {
        return
      }
      decrementDragCount()
    }

    /**
     * Drop handler.
     *
     * @param {DragEvent} e
     */
    function onDrop(e: DragEvent) {
      e.preventDefault()
      if (!isFileDrag(e)) {
        return
      }

      decrementDragCount()
      const files = getFileList(e)
      if (files.length > 0) {
        onFilesDropped(files)
      }
    }
  }, [onFilesDropped, useRefDep(targetRef)])

  // Returns a tuple of the state and the target ref.
  return [state, targetRef]

  /**
   * Increments the drag count. If we're just starting, dispatches
   * an action to notify of drag enter.
   */
  function incrementDragCount() {
    dragCount.current++
    if (dragCount.current === 1) {
      dispatch({ type: 'enter' })
      onEnter()
    }
  }

  /**
   * Decrements the drag count; dispatches an action
   * if it's time to notify of drag leave.
   */
  function decrementDragCount() {
    dragCount.current--
    if (dragCount.current === 0) {
      dispatch({ type: 'leave' })
      onLeave()
    }
  }
}

/**
 * Determines if the event is a file drag event.
 *
 * @param {DragEvent} e
 */
function isFileDrag(e: DragEvent) {
  if (!e.dataTransfer) {
    return false
  }
  if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
    return true
  }

  if (e.dataTransfer.files.length > 0) {
    return true
  }

  return false
}

/**
 * Gets the file list from a drag event.
 *
 * @param {DragEvent} e
 */
function getFileList(e: DragEvent): FileList {
  return e.dataTransfer!.files
}
